import CleTable, { type CleTableColumn } from "@cle/components/CleTable";
import {
  getAfgorelseDetailPath,
  getAfgorelseOverviewId,
  type AfgorelseOverviewModel,
} from "@/interfaces/afgorelse";
import { useNavigate } from "react-router";
import {
  formaterDokumentationsperiode,
  formaterIndberetningsfrist,
  formatDanishDate,
  getAfgorelseStatusPresentation,
} from "./afgorelseStatus";
import { formatLessons } from "@cle/utils/lessonLimits";

type AfgorelserTableProps = {
  afgorelser: AfgorelseOverviewModel[];
  isError?: boolean;
  isLoading?: boolean;
};

type AfgorelseTableRow = AfgorelseOverviewModel & {
  id: string;
};

const getAfgorelseTypeLabel = (afgorelse: AfgorelseOverviewModel): string =>
  afgorelse.afgorelseType === "paabud" ? "Påbud" : "Pålæg";

const AfgorelserTable = ({
  afgorelser,
  isError = false,
  isLoading = false,
}: AfgorelserTableProps) => {
  const navigate = useNavigate();
  const tableRows: AfgorelseTableRow[] = afgorelser.map((afgorelse) => ({
    ...afgorelse,
    id: getAfgorelseOverviewId(afgorelse),
  }));

  const columns: CleTableColumn<AfgorelseTableRow>[] = [
    {
      id: "type",
      header: "Type",
      cell: (afgorelse) => getAfgorelseTypeLabel(afgorelse),
    },
    {
      id: "documentationDeadline",
      header: "Dokumentationsperiode",
      cell: (afgorelse) =>
        formaterDokumentationsperiode(
          afgorelse.periodeFra,
          afgorelse.periodeTilMinusEnDag,
        ),
    },
    {
      id: "reportingDeadline",
      header: "Indberetningsfrist",
      cell: (afgorelse) => formaterIndberetningsfrist(afgorelse.frist),
    },
    {
      id: "status",
      header: "Status",
      cell: (afgorelse) => {
        const status = getAfgorelseStatusPresentation(afgorelse);

        return (
          <span className={`cle-status-badge ${status.className}`}>
            {status.label}
          </span>
        );
      },
    },
    {
      id: "lessons",
      header: "Lektionskrav",
      cell: (afgorelse) => formatLessons(afgorelse.paakraevedeLektioner),
    },
    {
      id: "reportedLessons",
      header: "Indberettede lektioner",
      cell: (afgorelse) =>
        formatLessons(afgorelse.lektionerIndberettet?.sum ?? 0),
    },
    {
      id: "submittedAt",
      header: "Indberetningsdato",
      cell: (afgorelse) => formatDanishDate(afgorelse.indsendt),
    },
  ];

  return (
    <CleTable
      columns={columns}
      data={tableRows}
      emptyDescription="Når afgørelser er tilgængelige, vises de her."
      emptyTitle="Ingen afgørelser fundet"
      errorMessage="Afgørelser kunne ikke indlæses lige nu."
      isError={isError}
      isLoading={isLoading}
      loadingRowCount={3}
      rowAction={{
        onClick: (_id, afgorelse) => {
          void navigate(
            getAfgorelseDetailPath(
              afgorelse.afgorelseType,
              getAfgorelseOverviewId(afgorelse),
            ),
          );
        },
      }}
    />
  );
};

export default AfgorelserTable;
